import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BookOpen, FileText, Video, Presentation, BarChart3, Calendar,
  CheckCircle2, Circle, Clock, TrendingUp, Award, Target, Zap,
  AlertCircle, Sparkles, Github, Send, MessageSquare, Brain, Loader2,
  Play, SkipForward, Edit, Plus, Check, X, ChevronRight, ChevronDown, Home, Eye, Download, Image,
  Network, BrainCircuit, Pencil, Layers, GitGraph, Move, Trophy, Star, Link2, ExternalLink, Youtube, Link
} from 'lucide-react';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import courseService from '../services/courseService';
import lessonPlanService from '../services/lessonPlanService';
import artifactService from '../services/artifactService';
import analyticsService from '../services/analyticsService';
import ArtifactRenderer from './artifacts/ArtifactRenderer';
import AnalyticsDashboard from './analytics/AnalyticsDashboard';
import ProjectWork from './ProjectWork';
import IntermediateTestsPanel from './IntermediateTestsPanel';
import '../styles/markdown.css';

const ToolConfigModal = ({ isOpen, onClose, tool, onGenerate }) => {
  const [count, setCount] = useState(tool?.type === 'sintesi' ? 'Concisa' : 'Standard');
  const [difficulty, setDifficulty] = useState('Medio');
  const [topic, setTopic] = useState('');

  if (!isOpen || !tool) return null;

  // Helper for option buttons
  const OptionButton = ({ label, value, current, setter }) => {
    const isActive = current === value;
    return (
      <button
        onClick={() => setter(value)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${isActive
          ? 'bg-blue-50 border-blue-200 text-blue-700'
          : 'border-gray-300 hover:bg-gray-50 text-gray-600'
          }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header - Compact */}
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            {tool.icon && typeof tool.icon === 'object' && <tool.icon className="w-5 h-5" />}
            Personalizza {tool.title}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body - Compact */}
        <div className="p-4 space-y-4">
          {tool.type === 'sintesi' ? (
            <div className="space-y-4">
              <label className="text-xs font-medium text-gray-700">Tipo di sintesi</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setCount('Concisa')}
                  className={`p-3 rounded-lg border text-center transition ${count === 'Concisa'
                    ? 'bg-blue-50 border-blue-500 text-blue-700 ring-1 ring-blue-500'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                >
                  <div className="font-semibold text-sm">Sintesi Contratta</div>
                  <div className="text-[10px] text-gray-400 mt-1">Essenziale e schematica</div>
                </button>
                <button
                  onClick={() => setCount('Approfondita')}
                  className={`p-3 rounded-lg border text-center transition ${count === 'Approfondita'
                    ? 'bg-blue-50 border-blue-500 text-blue-700 ring-1 ring-blue-500'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                    }`}
                >
                  <div className="font-semibold text-sm">Sintesi Approfondita</div>
                  <div className="text-[10px] text-gray-400 mt-1">Dettagliata e discorsiva</div>
                </button>
              </div>
            </div>
          ) : tool.type === 'mappa' ? (
            null
          ) : (
            /* Options Row - Default for other tools */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700">
                  {tool.type === 'flashcard' ? 'Numero di Flashcards' :
                    tool.type === 'quiz' ? 'Numero di Domande' :
                      'Numero di elementi'}
                </label>
                <div className="flex gap-2">
                  <OptionButton label="Small" value="Meno" current={count} setter={setCount} />
                  <OptionButton label="Medium" value="Standard" current={count} setter={setCount} />
                  <OptionButton label="Large" value="Più" current={count} setter={setCount} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700">Livello di difficoltà</label>
                <div className="flex gap-2">
                  <OptionButton label="Facile" value="Facile" current={difficulty} setter={setDifficulty} />
                  <OptionButton label="Medio" value="Medio" current={difficulty} setter={setDifficulty} />
                  <OptionButton label="Difficile" value="Difficile" current={difficulty} setter={setDifficulty} />
                </div>
              </div>
            </div>
          )}

          {/* Prompt Area */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">Quale dovrebbe essere l'argomento?</label>
            <div className="relative">
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full h-24 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm text-gray-700"
                placeholder="Descrivi l'argomento (es: 'La seconda guerra mondiale')..."
              />
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
              <p className="text-[10px] text-gray-500 font-medium mb-1">Cose da provare:</p>
              <ul className="text-[10px] text-gray-500 space-y-0.5 list-disc pl-3">
                <li>Limita a una fonte specifica (es. "Modulo 1")</li>
                <li>Concentrati sui concetti chiave</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 flex justify-end">
          <button
            onClick={() => onGenerate({ count, difficulty: tool.type === 'sintesi' ? count : difficulty, topic })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Genera
          </button>
        </div>
      </div>
    </div>
  );
};

const AlmaDashboard = () => {
  // Get course ID from URL
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error, info, warning, confirm } = useNotification();

  // Course State
  const [course, setCourse] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Modal State
  const [activeToolModal, setActiveToolModal] = useState(null); // { title: string, icon: Icon, type: string }
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Artifacts State
  const [artifacts, setArtifacts] = useState([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);

  const [expandedArtifact, setExpandedArtifact] = useState(null);
  const [showCompletionSuccess, setShowCompletionSuccess] = useState(null); // { score: number }

  // Analytics State
  const [analytics, setAnalytics] = useState(null);

  // Chat Hook
  const {
    messages: chatMessages,
    activeMode,
    setActiveMode,
    sendMessage: sendChatMessage,
    isLoading: isChatLoading,
    error: chatError,
    addLocalMessage,
  } = useChat(courseId);

  // Fetch Analytics for Sidebar/Header
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!courseId) return;
      try {
        const response = await analyticsService.getCourseAnalytics(courseId);
        if (response.success) {
          setAnalytics(response.analytics);
        }
      } catch (error) {
        console.error("Error fetching analytics for sidebar:", error);
      }
    };
    fetchAnalytics();
  }, [courseId]);



  // Helper function to detect if text contains markdown
  const hasMarkdown = (text) => {
    if (!text) return false;
    // Check for common markdown patterns
    const markdownPatterns = [
      /#{1,6}\s/,           // Headers
      /\*\*.*\*\*/,         // Bold
      /\*.*\*/,             // Italic
      /\[.*\]\(.*\)/,       // Links
      /```/,                // Code blocks
      /`.*`/,               // Inline code
      /^\s*[-*+]\s/m,       // Unordered lists
      /^\s*\d+\.\s/m,       // Ordered lists
      /^\s*>/m,             // Blockquotes
      /\|.*\|/,             // Tables
    ];
    return markdownPatterns.some(pattern => pattern.test(text));
  };

  // Helper to safely download/view files using authenticated fetch

  const handleFileAction = async (originalUrl, filename, view = false) => {
    const token = localStorage.getItem('auth_token');

    // Se è un link esterno (non inizia con il nostro host o è esplicitamente un link), apri direttamente
    const isExternal = originalUrl.startsWith('http') && !originalUrl.includes(window.location.host) && !originalUrl.includes('/api/');
    
    if (isExternal) {
        window.open(originalUrl, '_blank', 'noopener,noreferrer');
        return;
    }

    if (view) {
      try {
        const response = await fetch(originalUrl, {
          headers: {
            'Accept': 'application/json, */*',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          if (response.status === 403 || response.status === 401) {
            error('Accesso negato. Potrebbe essere necessario effettuare nuovamente il login o non hai i permessi per questo file.');
          } else if (response.status === 404) {
            error('File non trovato.');
          } else {
            error(`Errore durante la visualizzazione: ${response.statusText}`);
          }
          return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      } catch (error) {
        console.error('View error:', error);
        error('Errore durante la visualizzazione del file.');
      }

      return;
    }

    try {
      const response = await fetch(originalUrl, {
        headers: {
          'Accept': 'application/json, */*',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
      });

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          error('Accesso negato. Potrebbe essere necessario effettuare nuovamente il login o non hai i permessi per questo file.');
        } else {
          error(`Errore durante il download: ${response.statusText}`);
        }
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Download error:', error);
      error('Errore durante il download del file.');
    }
  };

  const handleMaterialDownload = async (material) => {
    try {
      if (!material?.id) {
        error('Materiale non valido.');
        return;
      }

      const response = await courseService.downloadCourseMaterial(courseId, material.id);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = material.fileName || material.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download error:', error);

      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        error('Accesso negato. Potrebbe essere necessario effettuare nuovamente il login o non hai i permessi per questo file.');
      } else if (status === 404) {
        error('File non trovato.');
      } else {
        error('Errore durante il download del file.');
      }
    }
  };

  // Helper function to get icon based on file extension
  const getFileIcon = (filename) => {
    if (!filename) return FileText;

    // Check if filename is an object/Url and convert to string
    const name = typeof filename === 'string' ? filename : (filename.url || '');
    const extension = name.split('.').pop().toLowerCase();

    // Documents
    if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'].includes(extension)) {
      return FileText;
    }

    // Presentations
    if (['ppt', 'pptx', 'odp', 'key'].includes(extension)) {
      return Presentation;
    }

    // Videos
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
      return Video;
    }

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) {
      return Image;
    }

    // YouTube / Video Links
    if (name.includes('youtube.com') || name.includes('youtu.be')) {
      return Video;
    }

    // Default
    return FileText;
  };

  // State Management
  const [activeTab, setActiveTab] = useState('tutor');
  const [compressionLevel, setCompressionLevel] = useState(3);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedTime, setSelectedTime] = useState('1h');
  const [lessonPlan, setLessonPlan] = useState([]);
  const [availableContents, setAvailableContents] = useState([]);
  const [courseObjectives, setCourseObjectives] = useState([]);
  const [objectivesLoading, setObjectivesLoading] = useState(false);

  // Lesson Plan State
  const [activeLessonPlan, setActiveLessonPlan] = useState(null);

  const normalizeLessonPlan = (plan) => {
    if (!plan) return null;

    const normalizedPlan = { ...plan };
    if (Array.isArray(normalizedPlan.artefacts)) {
      normalizedPlan.artefacts = normalizedPlan.artefacts.reduce((acc, art) => {
        acc[art.type] = art;
        return acc;
      }, {});
    }

    return normalizedPlan;
  };
  const [userProgress, setUserProgress] = useState(null); // { modules: [], ... }
  const [lessonPlanLoading, setLessonPlanLoading] = useState(true);
  const [selectedLessonsForPlan, setSelectedLessonsForPlan] = useState([]);
  const [userNote, setUserNote] = useState(''); // Insight Post-it

  // Load User Note (Scoped)
  useEffect(() => {
    const userId = user?.id || 'guest';
    const storageKey = `almaUserNote_${userId}_${courseId}`;
    const savedNote = localStorage.getItem(storageKey);
    setUserNote(savedNote || '');
  }, [courseId, user]);

  // Save functionality is moved to onChange handler to avoid race conditions on course switch

  // Modules and Lessons State (Synced with userProgress)
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState([]);

  // Initial Data Fetch
  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [courseId]);

  const loadCourseData = async () => {
    setLessonPlanLoading(true);
    try {
      // 1. Get Course Details
      try {
        const courseData = await courseService.getCourseById(courseId);
        setCourse(courseData);
      } catch (err) {
        console.error("Failed to load course details", err);
        setCourse({ title: `Corso #${courseId}`, id: courseId });
      }

      // 2. Get Progress (Modules)
      // Try getting modules from Progress API first
      let loadedModules = [];
      try {
        const progressData = await lessonPlanService.getProgress(courseId);
        if (progressData.success) {
          setUserProgress(progressData.progress);
          loadedModules = progressData.progress.modules || [];
        }
      } catch (e) {
        console.warn("Lesson Plan Progress API failed, falling back to basic Course Modules", e);
      }

      // Fallback: Get Basic Course Modules if Progress API didn't return any
      if (loadedModules.length === 0) {
        console.log("No modules from Progress API, fetching from CourseService...");
        try {
          loadedModules = await courseService.getCourseModules(courseId);
        } catch (e) {
          console.error("CourseService also failed", e);
          // Last resort: Initial Mock Data (if defined)
          console.warn("Using initial mock modules as fallback");
          loadedModules = initialModules;
        }
      }

      setModules(loadedModules);


      // 3. Get Active Plan
      try {
        const planData = await lessonPlanService.getActivePlan(courseId);
        if (planData.success && planData.lesson_plan) {
          setActiveLessonPlan(normalizeLessonPlan(planData.lesson_plan));
        } else {
          setActiveLessonPlan(null);
        }
      } catch (e) {
        console.warn("No active plan found or API error", e);
        setActiveLessonPlan(null);
      }

      // 4. Get Artifacts
      setArtifactsLoading(true);
      try {
        const loadedArtifacts = await artifactService.getArtifacts(courseId);
        // Map content.title (preferred) or name to title
        const mappedArtifacts = (loadedArtifacts || []).map(a => ({
          ...a,
          title: (a.content && a.content.title) || a.name
        }));
        setArtifacts(mappedArtifacts);
      } catch (err) {
        console.error("Failed to load artifacts", err);
      } finally {
        setArtifactsLoading(false);
      }

      // 5. Update Analytics (Sidebar/Header)
      try {
        const analyticsData = await analyticsService.getCourseAnalytics(courseId);
        if (analyticsData.success) {
          setAnalytics(analyticsData.analytics);
        }
      } catch (err) {
        console.error("Failed to refresh analytics", err);
      }

      // 6. Get Events (Calendar)
      setEventsLoading(true);
      try {
        const eventsData = await courseService.getCourseEvents(courseId);
        const now = new Date();
        // Filter future events and sort by date ascending
        const upcoming = eventsData
          .filter(e => new Date(e.end_at) >= now)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
          .slice(0, 3);
        setEvents(upcoming);
      } catch (err) {
        console.error("Failed to load events", err);
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }

    } catch (error) {
      console.error("Error loading lesson plan data:", error);
    } finally {
      setLessonPlanLoading(false);
    }
  };

  // Materials State
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const courseLinks = courseMaterials.filter((material) => material.type === 'link' && material.url);
  const visibleMaterials = courseMaterials.filter((material) => material.type !== 'link');

  // Tutor Chat State
  const [inputMessage, setInputMessage] = useState('');
  const chatEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Quiz State
  const [quizActive, setQuizActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showQuizResult, setShowQuizResult] = useState(false);

  // Data
  const materials = [
    {
      id: 1,
      type: 'pdf',
      title: 'Manuale Python Completo',
      progress: 35,
      icon: FileText,
      pages: '600 pag',
      size: '12.5 MB'
    },
    {
      id: 2,
      type: 'video',
      title: 'Video Tutorial (50 video)',
      progress: 20,
      icon: Video,
      duration: '15h',
      videos: 50
    },
    {
      id: 3,
      type: 'slides',
      title: 'Presentazioni Tematiche (20)',
      progress: 45,
      icon: Presentation,
      slides: '400',
      count: 20
    },
    {
      id: 4,
      type: 'pdf',
      title: 'PDF Approfondimento',
      progress: 10,
      icon: FileText,
      pages: '120 pag',
      size: '3.2 MB'
    }
  ];

  const timeOptions = [
    { value: '30min', label: '30 min' },
    { value: '1h', label: '1 ora' },
    { value: '2h', label: '2 ore' },
    { value: '3h+', label: '3+ ore' }
  ];

  const initialLessonPlan = [
    {
      id: 1,
      title: '📖 Cap 4: Funzioni Python - Sintesi (4.1-4.3)',
      time: '15 min',
      details: '📄 Manuale Python, pag. 45-67 | 🎯 Livello compressione: 3 (Bilanciato)',
      type: 'study',
      completed: false
    },
    {
      id: 2,
      title: '🗺️ Mappa Concettuale - Parametri e Return',
      time: '10 min',
      details: '🧠 Visualizza relazioni tra concetti | 🎨 Generazione automatica',
      type: 'tool',
      completed: false
    },
    {
      id: 3,
      title: '📝 Quiz di Verifica - Funzioni Base',
      time: '15 min',
      details: '🎯 10 domande adattive | ✅ Feedback immediato',
      type: 'assessment',
      completed: false
    },
    {
      id: 4,
      title: '💻 Esercizio Pratico - Funzioni con Return',
      time: '20 min',
      details: '🔨 Coding pratico | 💾 Commit su GitHub per correzione AI',
      type: 'practice',
      completed: false
    }
  ];

  const initialAvailableContents = [
    { id: 1, title: 'Cap 5: Liste e Tuple', info: '60 pag • 2h stimato', selected: false },
    { id: 2, title: 'Video: List Comprehension', info: '25 min • Intermedio', selected: false },
    { id: 3, title: 'PDF: Dizionari Avanzati', info: '15 pag • 45 min', selected: false },
    { id: 4, title: 'Esercizi: Set Operations', info: '20 esercizi • 1.5h', selected: false }
  ];

  const initialModules = [
    {
      id: 1,
      title: 'Intro Angular',
      completed: false,
      lessons: [
        { id: 1, title: 'Installazione dipendenze', completed: false },
        { id: 2, title: 'Avvio primo progetto', completed: false }
      ]
    },
    {
      id: 2,
      title: 'Componenti',
      completed: false,
      lessons: [
        { id: 3, title: 'Creare un componente', completed: false }
      ]
    },
    {
      id: 3,
      title: 'Direttive',
      completed: false,
      lessons: [
        { id: 4, title: 'NgIf e NgFor', completed: false },
        { id: 5, title: 'Direttive custom', completed: false }
      ]
    },
    {
      id: 4,
      title: 'Services e Dependency Injection',
      completed: false,
      lessons: [
        { id: 6, title: 'Creare un service', completed: false },
        { id: 7, title: 'Iniettare un service', completed: false },
        { id: 8, title: 'HTTP Client', completed: false }
      ]
    }
  ];

  const quizQuestions = [
    {
      question: 'Quale keyword si usa per definire una funzione in Python?',
      options: ['function', 'def', 'func', 'define'],
      correct: 1,
      explanation: 'In Python si usa "def" (abbreviazione di define) per definire una funzione.'
    },
    {
      question: 'Come si restituisce un valore da una funzione?',
      options: ['output', 'return', 'send', 'give'],
      correct: 1,
      explanation: 'La keyword "return" viene usata per restituire un valore da una funzione.'
    },
    {
      question: 'Cosa sono i parametri predefiniti?',
      options: [
        'Parametri obbligatori',
        'Parametri con valori di default',
        'Parametri che non possono essere modificati',
        'Parametri globali'
      ],
      correct: 1,
      explanation: 'I parametri predefiniti hanno valori di default e rendono gli argomenti opzionali.'
    },
    {
      question: 'Quanti valori può restituire una funzione Python?',
      options: ['Solo uno', 'Massimo due', 'Multipli (tramite tuple)', 'Nessuno'],
      correct: 2,
      explanation: 'Python può restituire multipli valori usando le tuple: return a, b, c'
    },
    {
      question: 'Qual è il modo corretto per chiamare una funzione senza argomenti?',
      options: ['funzione[]', 'funzione()', 'funzione{}', 'call funzione'],
      correct: 1,
      explanation: 'Le funzioni si chiamano usando le parentesi tonde: funzione()'
    }
  ];

  const strengths = [
    { name: 'Sintassi Base Python', score: 95, color: 'green' },
    { name: 'Strutture Dati (Liste)', score: 88, color: 'green' },
    { name: 'Control Flow (If/Else)', score: 92, color: 'green' }
  ];

  const weaknesses = [
    { name: 'List Comprehension', score: 62, color: 'yellow' },
    { name: 'Lambda Functions', score: 58, color: 'orange' },
    { name: 'Decoratori', score: 45, color: 'red' }
  ];

  const todayActivities = [
    { time: '09:00', activity: 'Capitolo 4: Funzioni (Paragrafi 4.1-4.3)', type: 'study', completed: true },
    { time: '09:45', activity: 'Mappa Concettuale: Parametri e Return', type: 'tool', completed: false },
    { time: '10:00', activity: 'Test di Verifica: Funzioni Base', type: 'assessment', completed: false },
    { time: '14:00', activity: 'Esercizi Pratici: List Comprehension', type: 'practice', completed: false }
  ];

  const compressionLevels = [
    { level: 1, label: 'Ultra-sintetica', desc: '3-5 bullet points', time: '5 min' },
    { level: 2, label: 'Compatta', desc: 'Paragrafi brevi', time: '10 min' },
    { level: 3, label: 'Bilanciata', desc: 'Esempi inclusi', time: '15 min' },
    { level: 4, label: 'Dettagliata', desc: 'Esempi multipli', time: '25 min' },
    { level: 5, label: 'Completa', desc: 'Tutto incluso', time: '30+ min' }
  ];

  // Initialize data
  useEffect(() => {
    setLessonPlan(initialLessonPlan);
    setAvailableContents(initialAvailableContents);
  }, []);

  // Load course objectives
  useEffect(() => {
    const loadObjectives = async () => {
      if (!courseId) return;

      setObjectivesLoading(true);
      try {
        const objectives = await courseService.getCourseObjectives(courseId);
        setCourseObjectives(objectives);
      } catch (error) {
        console.error('Errore caricamento obiettivi:', error);
        setCourseObjectives([]);
      } finally {
        setObjectivesLoading(false);
      }
    };

    loadObjectives();
  }, [courseId]);

  // Output consolidated into loadCourseData above


  // Polling for artifacts in creation
  useEffect(() => {
    // Check if any artifact is still generating (local temp only)
    const isGenerating = artifacts.some(a => a.id.toString().startsWith('temp-'));

    if (isGenerating) {
      console.log("Artifact generation in progress, polling...");
      const interval = setInterval(async () => {
        try {
          const updated = await artifactService.getArtifacts(courseId);
          if (updated && Array.isArray(updated)) {
            // MERGE POLICY: Keep local 'temp-' artifacts that are not in the server list yet.
            setArtifacts(prev => {
              const temps = prev.filter(a => a.id.toString().startsWith('temp-'));
              // Filter out temps if a real artifact with the same name exists in the update
              const uniqueTemps = temps.filter(t => !updated.some(u => u.name === t.name));
              return [...uniqueTemps, ...updated];
            });

            // Log status
            const remaining = updated.filter(a => a.content === null).length;
            if (remaining === 0) {
              console.log("All artifacts generated!");
            } else {
              console.log(`Still generating ${remaining} artifacts...`);
            }
          }
        } catch (e) {
          console.error("Polling error", e);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [artifacts, courseId]);

  // Main Handler for creating artifacts
  const handleCreateArtifact = async (config) => {
    // Extract config or use defaults
    const { topic, count, difficulty, includeChatHistory = true } = config;

    // Get type from arguments OR from the active modal state
    const type = config.type || (activeToolModal && activeToolModal.type) || 'mappa';
    const cleanTopic = topic || 'Argomento Generico';

    // Close modal
    setActiveToolModal(null);

    // Validate
    if (!type) {
      error("Errore: Tipo di artefatto non specificato.");
      return;
    }

    // 1. Map parameters to API specs
    const options = {};

    if (type === 'quiz') {
      // Map 'count' (Meno/Standard/Più) -> quiz_size (small/medium/large)
      const sizeMap = { 'Meno': 'small', 'Standard': 'medium', 'Più': 'large' };
      options.quiz_size = sizeMap[count] || 'medium';

      // Map 'difficulty' (Facile/Medio/Difficile) -> quiz_difficulty (facile/intermedio/difficile)
      const diffMap = { 'Facile': 'facile', 'Medio': 'intermedio', 'Difficile': 'difficile' };
      options.quiz_difficulty = diffMap[difficulty] || 'intermedio';
    }
    else if (type === 'flashcard') {
      // Flashcards use similar sizing/difficulty if backend supports it, otherwise default
      // Assuming flashcards API mimics quiz or uses generic params
      const sizeMap = { 'Meno': 'small', 'Standard': 'medium', 'Più': 'large' };
      options.flashcard_size = sizeMap[count] || 'medium'; // Guessing param name or maybe it reuses quiz_size? User didn't specify.
      // Safest is to put it in prompt too or generic params
    }
    else if (type === 'sintesi') {
      // Map 'count' (Concisa/Approfondita) -> synthesis_level (contratta/estesa)
      // Note: 'count' state holds the label value from UI
      const levelMap = { 'Concisa': 'contratta', 'Approfondita': 'estesa' };
      options.synthesis_level = levelMap[count] || 'contratta';
    }

    // Include chat history if requested
    if (includeChatHistory) {
      options.chat_history = chatMessages.map(m => ({ role: m.role, content: m.content }));
    }

    // 2. Construct User Prompt
    // User request example: "Crea un quiz su React Hooks"
    const userPrompt = `Crea ${type === 'mappa' ? 'una mappa concettuale' : type === 'flashcard' ? 'delle flashcards' : 'un ' + type} su "${cleanTopic}"`;

    try {
      // Optimistic Update
      const tempId = 'temp-' + Date.now();
      const tempArtifact = {
        id: tempId,
        course_id: courseId,
        name: userPrompt,
        type: type,
        content: null,
        created_at: new Date().toISOString()
      };

      setArtifacts(prev => [tempArtifact, ...prev]);

      // API Call
      await artifactService.createArtifact(courseId, type, userPrompt, options);

      // Force immediate refresh to replace temp with real ID (or wait for poll)
      // Polling will pick it up, but a quick refresh ensures we have the real DB ID if needed immediately.
      const fresh = await artifactService.getArtifacts(courseId);
      setArtifacts(fresh);

    } catch (err) {
      console.error("Creation failed", err);
      error(`Impossibile creare il contenuto. Errore: ${err.response?.data?.message || err.message || 'Sconosciuto'}`);
      // Remove temp artifact on failure
      setArtifacts(prev => prev.filter(a => a.id.toString().substring(0, 5) !== 'temp-'));
    }
  };


  const handleDeleteArtifact = async (e, id) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: 'Elimina Contenuto',
      message: 'Sei sicuro di voler eliminare questo contenuto? L\'azione è irreversibile.',
      confirmText: 'Elimina',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await artifactService.deleteArtifact(id);
      setArtifacts(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error("Delete failed", err);
      error("Errore durante l'eliminazione.");
    }
  };

  const handleRenameArtifact = async (id, newName) => {
    try {
      await artifactService.updateArtifact(id, { name: newName });
      setArtifacts(prev => prev.map(a => a.id === id ? { ...a, name: newName } : a));
    } catch (err) {
      console.error("Rename failed", err);
      error("Errore durante la rinomina.");
    }
  };

  // Auto-expand first incomplete module
  useEffect(() => {
    if (modules.length > 0 && expandedModules.length === 0) {
      // Find first incomplete module
      const firstIncompleteModule = modules.find(m => !m.completed);
      if (firstIncompleteModule) {
        setExpandedModules([firstIncompleteModule.id]);
      }
    }
  }, [modules]);

  // Load course materials
  useEffect(() => {
    const loadMaterials = async () => {
      if (!courseId) return;

      setMaterialsLoading(true);
      try {
        const materialsData = await courseService.getCourseMaterials(courseId);
        setCourseMaterials(materialsData);
      } catch (error) {
        console.error('Errore caricamento materiali:', error);
        // Fallback or empty state could be handled here
        setCourseMaterials([]);
      } finally {
        setMaterialsLoading(false);
      }
    };

    loadMaterials();
  }, [courseId]);

  // Convert messages from API format (role/content) to UI format (type/text)
  const messages = chatMessages.map(msg => ({
    type: msg.role === 'user' ? 'user' : 'ai',
    text: msg.content,
    timestamp: msg.timestamp
  }));

  // Tutor Chat Functions
  // Tutor Chat Functions
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const text = inputMessage.trim();
    // Regex to detect "Genera [tool] su [topic]"
    const regex = /^genera\s+(?:un|una|il|la)?\s*(mappa|sintesi|quiz|flashcard|flashcards)\s+(?:su|di|sull'|sul|sulla)\s+(.+)$/i;
    const match = text.match(regex);

    if (match) {
      const toolTypeRaw = match[1].toLowerCase();
      const topic = match[2];

      let toolType = 'sintesi';
      if (toolTypeRaw.includes('mappa')) toolType = 'mappa';
      else if (toolTypeRaw.includes('quiz')) toolType = 'quiz';
      else if (toolTypeRaw.includes('flashcard')) toolType = 'flashcard';

      // 1. Add user message locally
      addLocalMessage('user', text);
      setInputMessage('');

      // 2. Add AI response locally
      const toolName = toolType === 'mappa' ? 'Mappa Concettuale' :
        toolType === 'quiz' ? 'Quiz' :
          toolType === 'flashcard' ? 'Flashcards' : 'Sintesi';

      addLocalMessage('ai', `Certamente! Sto generando ${toolName === 'Flashcards' ? 'le' : 'una'} ${toolName} su "${topic}".\n\nControlla la sezione **Tool AI** tra qualche istante per vederlo completato.`);

      // 3. Trigger generation
      handleCreateArtifact({
        type: toolType,
        topic: topic,
        count: toolType === 'sintesi' ? 'Concisa' : (toolType === 'quiz' || toolType === 'flashcard' ? 'Standard' : undefined),
        difficulty: 'Medio'
      });

    } else {
      // Normal chat message
      setInputMessage('');
      try {
        await sendChatMessage(text);
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const quickAction = (action) => {
    // Toggle tool: if already active, deactivate it; otherwise activate it
    if (activeMode === action) {
      setActiveMode(null);
    } else {
      setActiveMode(action);
    }
  };

  // Lesson Plan Functions
  // Legacy generatePlan removed - replaced by handleGeneratePlan


  const startLesson = (lessonId) => {
    setLessonPlan(lessonPlan.map(lesson =>
      lesson.id === lessonId ? { ...lesson, completed: true } : lesson
    ));
  };

  const skipLesson = (lessonId) => {
    // Lesson skipped - user can ask the chat for alternatives if needed
  };

  const toggleContent = (contentId) => {
    setAvailableContents(availableContents.map(content =>
      content.id === contentId ? { ...content, selected: !content.selected } : content
    ));
  };

  const addToPlan = () => {
    const selected = availableContents.filter(c => c.selected);
    if (selected.length === 0) {
      warning('Non hai selezionato nessun contenuto. Scegli gli argomenti che vuoi aggiungere al tuo piano!');
      return;
    }

    // Add selected contents to plan (implementation would go here)
    console.log('Adding to plan:', selected);

    setAvailableContents(availableContents.map(c => ({ ...c, selected: false })));
  };

  // Module Functions
  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const expandAllModules = () => {
    setExpandedModules(modules.map(m => m.id));
  };

  const collapseAllModules = () => {
    setExpandedModules([]);
  };

  const toggleModule = (moduleId) => {
    setModules(modules.map(module => {
      if (module.id === moduleId) {
        const newCompleted = !module.completed;
        // If marking module as complete, mark all lessons as complete
        // If marking module as incomplete, mark all lessons as incomplete
        return {
          ...module,
          completed: newCompleted,
          lessons: module.lessons.map(lesson => ({ ...lesson, completed: newCompleted }))
        };
      }
      return module;
    }));
  };

  const toggleLesson = (moduleId, lessonId) => {
    setModules(modules.map(module => {
      if (module.id === moduleId) {
        const updatedLessons = module.lessons.map(lesson =>
          lesson.id === lessonId ? { ...lesson, completed: !lesson.completed } : lesson
        );
        // Check if all lessons are completed
        const allLessonsCompleted = updatedLessons.every(lesson => lesson.completed);
        return {
          ...module,
          lessons: updatedLessons,
          completed: allLessonsCompleted
        };
      }
      return module;
    }));
  };

  // Quiz Functions
  const startQuiz = () => {
    setQuizActive(true);
    setCurrentQuestion(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowQuizResult(false);
  };

  const selectAnswer = (index) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(index);
      if (index === quizQuestions[currentQuestion].correct) {
        setQuizScore(quizScore + 1);
      }
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setShowQuizResult(true);
    }
  };

  const closeQuiz = () => {
    setQuizActive(false);
    const percentage = Math.round((quizScore / quizQuestions.length) * 100);

    // Quiz completed - score is displayed in the modal
    console.log('Quiz completed with score:', quizScore, '/', quizQuestions.length, `(${percentage}%)`);
  };

  // Render Functions
  const renderTutorTab = () => (
    <div className="space-y-6">
      {/* Tutor Panel */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-xl h-[calc(100vh-340px)] flex flex-col">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
            <Brain className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold">ALMA - Il Tuo Tutor Personale</h3>
            <p className="text-blue-200 text-sm">Assistente AI per ottimizzare il tuo apprendimento</p>
          </div>
        </div>

        {/* Chat Messages */}
        {/* Chat Messages */}
        <div className="bg-white bg-opacity-15 backdrop-blur rounded-lg p-4 mb-4 flex-1 overflow-y-auto min-h-0">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`mb-3 p-3 rounded-lg text-sm ${msg.type === 'user'
                ? 'bg-white bg-opacity-30 ml-8'
                : 'bg-white bg-opacity-20'
                }`}
            >
              <div className="flex items-start gap-2">
                {msg.type === 'ai' && <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <div className="flex-1">
                  {msg.type === 'ai' && hasMarkdown(msg.text) ? (
                    <div className="markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Active Tool Display */}
        {activeMode && (
          <div className="bg-white bg-opacity-25 backdrop-blur rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-300" />
              <div>
                <span className="font-semibold block">
                  Modalità Attiva: {
                    activeMode === 'quiz' ? '📝 Genera Quiz' :
                      activeMode === 'summary' ? '📄 Crea Sintesi' :
                        activeMode === 'plan' ? '📅 Pianifica Studio' :
                          activeMode === 'explain' ? '💡 Spiega Concetto' : ''
                  }
                </span>
                <span className="text-sm text-blue-100">
                  {
                    activeMode === 'quiz' ? 'Creo test adattivi basati sulle tue lacune' :
                      activeMode === 'summary' ? 'Genero sintesi ottimizzate dei contenuti' :
                        activeMode === 'plan' ? 'Organizzo il tuo studio in base al tempo' :
                          activeMode === 'explain' ? 'Semplifico concetti complessi con esempi' : ''
                  }
                </span>
              </div>
            </div>
            <button
              onClick={() => setActiveMode(null)}
              className="p-1 bg-white bg-opacity-30 hover:bg-opacity-40 rounded-full transition"
              title="Disattiva modalità"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Chat Error Display */}
        {chatError && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded-lg flex items-center justify-between">
            <span>{chatError}</span>
            <button
              onClick={() => {/* Error will be cleared on next send */ }}
              className="text-red-700 hover:text-red-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Input */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isChatLoading && sendMessage()}
            placeholder={activeMode ? `Scrivi il messaggio per la modalità attiva...` : "Chiedimi qualsiasi cosa..."}
            className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
            disabled={isChatLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isChatLoading}
            className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChatLoading ? (
              <>
                <Circle className="w-4 h-4 animate-spin" />
                Invio...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Invia
              </>
            )}
          </button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { key: 'quiz', icon: '📝', label: 'Genera Quiz' },
            { key: 'summary', icon: '📄', label: 'Crea Sintesi' },
            { key: 'plan', icon: '📅', label: 'Pianifica Studio' },
            { key: 'explain', icon: '💡', label: 'Spiega Concetto' }
          ].map(action => (
            <button
              key={action.key}
              onClick={() => quickAction(action.key)}
              disabled={isChatLoading}
              className={`px-3 py-2 border rounded-lg text-sm transition disabled:opacity-50 disabled:cursor-not-allowed ${activeMode === action.key
                ? 'bg-white text-blue-600 border-white shadow-lg scale-105 font-semibold'
                : 'bg-white bg-opacity-20 border-white border-opacity-30 hover:bg-opacity-30'
                }`}
            >
              <span className="mr-1">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      </div>


    </div>
  );

  // Lesson Plan Handlers
  const toggleLessonSelection = (lessonId) => {
    setSelectedLessonsForPlan(prev => {
      if (prev.includes(lessonId)) {
        return prev.filter(id => id !== lessonId);
      } else {
        if (prev.length >= 3) {
          setShowLimitModal(true);
          return prev;
        }
        return [...prev, lessonId];
      }
    });
  };

  const handleGeneratePlan = async () => {
    if (selectedLessonsForPlan.length === 0) {
      warning("Seleziona almeno una lezione per generare il piano.");
      return;
    }

    // 1. Immediate UI Feedback: Manually set "Generating" state
    setActiveLessonPlan({
      is_generating: true,
      completed: false,
      artefacts: {
        sintesi: { content: null },
        mappa: { content: null },
        flashcard: { content: null },
        quiz: { content: null }
      }
    });

    // 2. Start Generation safely
    try {
      // Don't set loading true, as we want to show the checklist, not the spinner
      const data = await lessonPlanService.generatePlan(courseId, selectedLessonsForPlan);

      if (data.success) {
        // Update with real ID and state from server
        // This won't interrupt the generation view because is_generating is likely still true
        setActiveLessonPlan(data.lesson_plan);
        startPlanPolling();
      }
    } catch (error) {
      if (error.status === 409) {
        // Plan already exists
        info("Esiste già un piano di studio attivo (non completato).");
        loadCourseData();
      } else {
        console.error("Error generating plan:", error);
        error("Errore durante la generazione");
        setActiveLessonPlan(null); // Reset UI on error
      }
    }
  };

  // Handle Plan Quiz Completion
  const handlePlanQuizCompletion = async (results) => {
    // results: { percentage, score, total, passed }
    try {
      setLessonPlanLoading(true);

      // 1. Call Completion Endpoint for ALL attempts (pass or fail)
      await analyticsService.completeQuiz({
        course_id: parseInt(courseId, 10),
        artefact_id: expandedArtifact?.id,
        percentage: results.percentage,
        score: results.score,
        total_questions: results.total,
        passed: results.passed,
        answers: results.answers || [] // Include detailed answers
      });

      // 2. Show Success Modal ONLY if passed
      if (results.passed) {
        setShowCompletionSuccess({ score: results.percentage });
      }

      setExpandedArtifact(null); // Close quiz modal

      // 3. Reload data to show updated stats
      await loadCourseData();

    } catch (error) {
      console.error("Error completing quiz:", error);
      error(`Errore: ${error.message || "Errore sconosciuto durante il salvataggio."}`);
    } finally {
      setLessonPlanLoading(false);
    }
  };

  const startPlanPolling = () => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    let attempts = 0;
    const maxAttempts = 100; // 100 * 3s = 300s (5 min) timeout

    pollingIntervalRef.current = setInterval(async () => {
      attempts++;
      try {
        const data = await lessonPlanService.getActivePlan(courseId);
        console.log("Polling Plan Response:", data); // DEBUG

        if (data.lesson_plan) {
          const normalizedPlan = normalizeLessonPlan(data.lesson_plan);

          // ALWAYS normalize artifacts for the UI (both in progress and finished)
          if (Array.isArray(data.lesson_plan.artefacts)) {
            // Capture the array reference BEFORE mutation for the async state update
            const artefactsArray = data.lesson_plan.artefacts;

            // 1. Update Global Artifacts (Tools Tab) - Merge new ones
            setArtifacts(prev => {
              // Use the captured 'artefactsArray' which is guaranteed to be an array
              const newArtifacts = artefactsArray.map(a => ({
                ...a,
                title: (a.content && a.content.title) || a.name
              }));
              const existingIds = new Set(prev.map(a => a.id));
              const uniqueNew = newArtifacts.filter(a => !existingIds.has(a.id));
              return [...uniqueNew, ...prev];
            });

            // 2. Normalize for Lesson Plan View (Cards/Checklist)
            // It is safe to mutate data.lesson_plan here for setActiveLessonPlan below, 
            // as setActiveLessonPlan is not using a callback dependent on the previous state of 'data'
            normalizedPlan.artefacts = artefactsArray.reduce((acc, art) => {
              acc[art.type] = art;
              return acc;
            }, {});
          }

          // Update UI with current state (even if generating)
          console.log("Updating Active Lesson Plan:", normalizedPlan); // DEBUG
          setActiveLessonPlan(normalizedPlan);

          if (normalizedPlan.generation_status === 'failed') {
            console.error("Lesson plan generation failed:", normalizedPlan.generation_error);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            error("Errore durante la generazione");
            return;
          }

          // Stop polling only if finished AND quiz has content
          // Note: artefacts is now an object (map) due to normalization above lines 1331-1334
          const artefacts = normalizedPlan.artefacts || {};
          const quizArtifact = artefacts['quiz'];
          const hasQuizContent = quizArtifact && quizArtifact.content;

          if (!normalizedPlan.is_generating && hasQuizContent) {
            console.log("Plan generation finished & Quiz ready, stopping polling."); // DEBUG
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          } else if (!normalizedPlan.is_generating) {
            console.log("Plan generation flagged done, but Quiz content missing. Continuing polling...");
          }
        } else {
          // If the backend deleted the lesson plan because it failed, we receive null
          console.error("Lesson plan non trovato durante il polling, probabile errore di generazione.");
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setActiveLessonPlan(null);
          error("Errore durante la generazione");
          await loadCourseData(); // Ricarica lo stato pulito
        }
      } catch (e) {
        console.error("Polling error", e);
      }

      if (attempts >= maxAttempts) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        console.warn("Polling timeout reached");
        setActiveLessonPlan(null);
        error("Errore durante la generazione");
      }
    }, 3000);
  };

  const renderLessonSelectionCard = (isSelectionMode = true) => (
    <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isSelectionMode ? 'Crea il tuo Piano di Studio' : 'Struttura del Corso'}
        </h2>
        {isSelectionMode && (
          <p className="text-gray-600 max-w-xl mx-auto">
            Seleziona lezioni specifiche su cui vuoi concentrarti. L'AI genererà materiali di studio personalizzati (sintesi, quiz, flashcard) solo per questi argomenti.
          </p>
        )}
      </div>

      {!modules.length ? (
        <div className="text-center py-8 text-gray-500">Nessun modulo disponibile.</div>
      ) : (
        <div className="space-y-4 mb-8">
          {modules.map(module => (
            <div key={module.id} className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 p-3 font-bold text-gray-700 flex justify-between items-center">
                <span>{module.title}</span>
                {module.completed && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Completato</span>}
              </div>
              <div className="divide-y">
                {module.lessons.map(lesson => {
                  const isSelectable = !lesson.completed && isSelectionMode;
                  return (
                    <div
                      key={lesson.id}
                      className={`p-3 flex items-center gap-3 transition-colors ${isSelectable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed opacity-60'
                        } ${isSelectionMode && selectedLessonsForPlan.includes(lesson.id) ? 'bg-blue-50' : ''}`}
                      onClick={() => isSelectable && toggleLessonSelection(lesson.id)}
                    >
                      {isSelectionMode && (
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition ${lesson.completed
                          ? 'bg-green-100 border-green-500' // Completed style
                          : selectedLessonsForPlan.includes(lesson.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300'
                          }`}>
                          {(lesson.completed || selectedLessonsForPlan.includes(lesson.id)) && (
                            <Check className={`w-3 h-3 ${lesson.completed ? 'text-green-600' : 'text-white'}`} />
                          )}
                        </div>
                      )}
                      {!isSelectionMode && (
                        <div className="w-5 h-5 flex items-center justify-center">
                          <div className={`w-1.5 h-1.5 rounded-full ${lesson.completed ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        </div>
                      )}
                      <span className={lesson.completed ? 'text-gray-500 font-medium' : 'text-gray-800'}>
                        {lesson.title} {lesson.completed && '(Completata)'}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {isSelectionMode && (
        <div className="flex justify-end sticky bottom-6">
          <button
            onClick={handleGeneratePlan}
            disabled={selectedLessonsForPlan.length === 0}
            className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold shadow-xl hover:bg-blue-700 transition transform hover:scale-105 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Genera Lesson Plan ({selectedLessonsForPlan.length})
          </button>
        </div>
      )}
    </div>
  );

  const renderLessonPlanTab = () => {
    // 1. Loading State
    if (lessonPlanLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600">Caricamento piano di studio...</p>
        </div>
      );
    }

    // 2. Generating State (Checklist)
    if (activeLessonPlan && activeLessonPlan.is_generating) {
      return (
        <div className="bg-white rounded-xl p-8 shadow-lg text-center max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Brain className="w-10 h-10 text-blue-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">L'AI sta creando il tuo piano</h2>
          <p className="text-gray-600 mb-6">
            Sto analizzando i contenuti e generando i materiali di studio personalizzati.
          </p>

          <div className="w-full max-w-sm mx-auto mb-8 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="space-y-3">
              {[
                { type: 'sintesi', label: 'Sintesi' },
                { type: 'mappa', label: 'Mappa Concettuale' },
                { type: 'flashcard', label: 'Flashcard' },
                { type: 'quiz', label: 'Quiz' }
              ].map((item) => {
                const isReady = activeLessonPlan.artefacts && activeLessonPlan.artefacts[item.type] && activeLessonPlan.artefacts[item.type].content;
                return (
                  <div key={item.type} className="flex items-center justify-between p-2 rounded-lg bg-white shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isReady ? 'bg-green-100 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                        {isReady ? <Check className="w-5 h-5" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      <span className={`font-medium ${isReady ? 'text-gray-900' : 'text-gray-500'}`}>{item.label}</span>
                    </div>
                    {isReady && <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">Pronto</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-6">Puoi continuare a navigare, ti avviserò quando è pronto.</p>
          <button
            onClick={async () => {
              const confirmed = await confirm({
                title: 'Annulla Generazione',
                message: 'Sei sicuro di voler annullare la generazione del piano di studi?',
                confirmText: 'Sì, annulla',
                cancelText: 'No, continua',
                type: 'warning'
              });

              if (!confirmed) return;

              try {
                // Clear polling immediately to avoid "Plan not found" error during deletion
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }

                if (activeLessonPlan && activeLessonPlan.id) {
                  await lessonPlanService.cancelPlan(activeLessonPlan.id);
                }
                setActiveLessonPlan(null);
                // Force refresh of course data to ensure clean state
                loadCourseData();
                success("Generazione annullata");
              } catch (err) {
                console.error("Error cancelling plan:", err);
                error("Errore durante la generazione");
                // Fallback: reset local state anyway if user wants to force out
                setActiveLessonPlan(null);
              }
            }}
            className="text-sm text-red-500 hover:text-red-700 underline transition"
          >
            Annulla generazione
          </button>
        </div>
      );
    }

    if (activeLessonPlan && activeLessonPlan.generation_status === 'failed') {
      return (
        <div className="bg-white rounded-xl p-8 shadow-lg text-center max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Brain className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Generazione non completata</h2>
          <p className="text-gray-600 mb-4">
            Il piano di studio esiste ancora, ma il worker ha restituito un errore durante la creazione dei materiali.
          </p>
          <div className="text-left bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
            <p className="text-sm font-semibold text-red-700 mb-1">Dettaglio errore</p>
            <p className="text-sm text-red-900 break-words">
              {activeLessonPlan.generation_error || 'Errore non disponibile'}
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                // Clear polling immediately
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }

                if (activeLessonPlan && activeLessonPlan.id) {
                  await lessonPlanService.cancelPlan(activeLessonPlan.id);
                }
                setActiveLessonPlan(null);
                loadCourseData();
                success("Generazione annullata");
              } catch (serviceError) {
                console.error("Error removing failed plan:", serviceError);
                error("Errore durante la generazione");
              }
            }}
            className="text-sm text-red-600 hover:text-red-800 underline transition"
          >
            Elimina il piano fallito
          </button>
        </div>
      );
    }

    // 3. Active Plan State (Ready and Incomplete)
    if (activeLessonPlan && !activeLessonPlan.completed) {
      return (
        <div className="space-y-6">
          {/* Always show Lesson Selection Card above Active Plan (Read Only) */}
          {renderLessonSelectionCard(false)}

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Target className="w-6 h-6 text-blue-600" />
                Il Tuo Piano di Studio
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-600">
                  {activeLessonPlan.completion_percentage || 0}% Completato
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${activeLessonPlan.completion_percentage || 0}%` }}
              ></div>
            </div>

            {/* Lessons List */}
            <div className="space-y-3">
              {activeLessonPlan.lessons.map((lesson, index) => (
                <div key={lesson.id} className="border border-gray-100 rounded-lg p-4 hover:shadow-md transition bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                        Lezione {index + 1} • {lesson.module_title || `Modulo ${lesson.id}`}
                      </div>
                      <h4 className="font-bold text-gray-800 text-lg mb-2">{lesson.title}</h4>
                    </div>
                    {!lesson.completed ? (
                      <button
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
                        onClick={() => {/* Navigate to lesson content or quiz */ }}
                      >
                        Inizia
                      </button>
                    ) : (
                      <div className="text-green-600 font-bold text-sm">
                        Score: {lesson.quiz_score}%
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Artefacts Section */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Materiali di Supporto Generati</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['sintesi', 'mappa', 'flashcard', 'quiz'].map(type => {
                  const artefact = activeLessonPlan.artefacts && activeLessonPlan.artefacts[type];
                  return (
                    <button
                      key={type}
                      disabled={!artefact}
                      onClick={() => artefact && setExpandedArtifact({
                        name: type.charAt(0).toUpperCase() + type.slice(1),
                        ...artefact,
                        onComplete: type === 'quiz' ? handlePlanQuizCompletion : undefined
                      })}
                      className={`text-left p-4 rounded-lg border-2 transition-all w-full flex items-center justify-between group ${artefact
                        ? 'border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 hover:shadow-md cursor-pointer'
                        : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${artefact
                          ? 'bg-blue-600 text-white group-hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-400'}`}>
                          {activeLessonPlan.is_generating && !artefact ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            type === 'sintesi' ? <FileText className="w-5 h-5" /> :
                              type === 'mappa' ? <GitGraph className="w-5 h-5" /> :
                                type === 'flashcard' ? <Layers className="w-5 h-5" /> :
                                  <Check className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-gray-800 capitalize">
                            {type === 'mappa' ? 'Mappa' : type}
                          </div>
                          <div className="text-xs text-gray-500">
                            {artefact ? 'Pronto per lo studio' : 'Non disponibile'}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${artefact ? 'text-blue-400 group-hover:text-blue-600' : 'text-gray-300'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 4. No Plan State (Selection Mode)
    return (
      <div className="space-y-6">
        {renderLessonSelectionCard()}
      </div>
    );
  };

  const renderMaterialsTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-blue-600" />
          Materiali Didattici
        </h2>

        {materialsLoading ? (
          <div className="text-center py-8">
            <Circle className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            <p className="text-sm text-gray-500 mt-3">Caricamento materiali...</p>
          </div>
          ) : visibleMaterials.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nessun materiale disponibile per questo corso</p>
            </div>
          ) : (
            <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {/* File Section */}
              {visibleMaterials.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Documenti e File</h3>
                  {visibleMaterials.map((material, idx) => {
                    const Icon = getFileIcon(material.fileName || material.url || '');
                    return (
                      <div
                        key={material.id || idx}
                        className="p-4 border-2 rounded-lg hover:border-blue-400 hover:shadow-md transition border-gray-200"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Icon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-800 break-words">{material.title}</div>
                              <div className="text-sm text-gray-500 capitalize">
                                {material.type || 'Documento'}
                                {material.createdAt && ` • ${new Date(material.createdAt).toLocaleDateString()}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleFileAction(material.url, material.fileName || material.title, true)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 font-semibold self-start"
                            >
                              <Eye className="w-4 h-4" />
                              Visualizza
                            </button>
                            <button
                              onClick={() => handleMaterialDownload(material)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 font-semibold self-start"
                            >
                              <Download className="w-4 h-4" />
                              Scarica
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Links Section */}
              {courseLinks.length > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Link Esterni e Video</h3>
                  {courseLinks.map((link, idx) => {
                    const isYoutube = link.url.includes('youtube.com') || link.url.includes('youtu.be');
                    const Icon = isYoutube ? Youtube : Link;
                    return (
                      <div
                        key={link.id || idx}
                        className="p-4 border-2 rounded-lg hover:border-indigo-400 hover:shadow-md transition border-gray-100 bg-indigo-50/30"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Icon className={`w-6 h-6 ${isYoutube ? 'text-red-500' : 'text-indigo-600'} flex-shrink-0 mt-1`} />
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-800 break-words">{link.title}</div>
                              <div className="text-sm text-gray-500">
                                {isYoutube ? 'Video YouTube' : 'Link Esterno'}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 font-semibold self-start"
                            >
                              <Eye className="w-4 h-4" />
                              Visualizza
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
      </div>


    </div >
  );

  const renderToolsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            icon: FileText,
            title: 'Sintesi Intelligente',
            bgClass: 'bg-indigo-50 hover:bg-indigo-100',
            textClass: 'text-indigo-700',
            iconClass: 'text-indigo-600',
            action: () => setActiveToolModal({
              title: 'Sintesi Intelligente',
              icon: FileText,
              type: 'sintesi'
            }),
            toolType: 'sintesi'
          },
          {
            icon: Network,
            title: 'Mappe Concettuali',
            bgClass: 'bg-fuchsia-50 hover:bg-fuchsia-100',
            textClass: 'text-fuchsia-700',
            iconClass: 'text-fuchsia-600',
            action: () => setActiveToolModal({
              title: 'Mappe Concettuali',
              icon: Network,
              type: 'mappa'
            }),
            toolType: 'mappa'
          },
          {
            icon: BrainCircuit,
            title: 'Flash Cards',
            bgClass: 'bg-orange-50 hover:bg-orange-100',
            textClass: 'text-orange-700',
            iconClass: 'text-orange-600',
            action: () => setActiveToolModal({
              title: 'Flash Cards',
              icon: BrainCircuit,
              type: 'flashcard'
            }),
            toolType: 'flashcard'
          },
          {
            icon: Target,
            title: 'Assessment Adattivo',
            bgClass: 'bg-sky-50 hover:bg-sky-100',
            textClass: 'text-sky-700',
            iconClass: 'text-sky-600',
            action: () => setActiveToolModal({
              title: 'Assessment Adattivo',
              icon: Target,
              type: 'quiz'
            }),
            toolType: 'quiz'
          }
        ].map((tool, idx) => {
          const Icon = tool.icon;
          return (
            <div
              key={idx}
              onClick={tool.action}
              className={`${tool.bgClass} rounded-2xl p-4 flex items-center justify-between transition-all cursor-pointer border border-gray-200 hover:border-${tool.textClass.split('-')[1]}-300 hover:shadow-md`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-white bg-opacity-60`}>
                  {typeof tool.icon === 'string'
                    ? <span className="text-2xl">{tool.icon}</span>
                    : <Icon className={`w-6 h-6 ${tool.iconClass}`} />
                  }
                </div>
                <div>
                  <h3 className={`font-bold ${tool.textClass} text-lg`}>{tool.title}</h3>
                  <p className="text-gray-500 text-xs">AI Assistant</p>
                </div>
              </div>
              <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm`}>
                <Plus className={`w-4 h-4 ${tool.iconClass}`} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 border-b pb-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Contenuti Generati dall'AI
        </h3>

        {artifacts.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <Brain className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nessun contenuto generato ancora.</p>
            <p className="text-xs text-gray-400">Usa gli strumenti qui sopra per creare mappe, riassunti e quiz.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artifacts.map((artifact) => {
              // Handle Loading State (content is null)
              const isCreating = !artifact.content;

              if (isCreating) {
                return (
                  <div key={artifact.id} className="bg-white rounded-2xl border border-blue-100 p-6 flex flex-col items-center justify-center text-center h-[220px] relative overflow-hidden">
                    <div className="absolute inset-0 bg-blue-50/30 animate-pulse"></div>
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                      <h3 className="font-bold text-gray-700 text-sm mb-1 px-4 line-clamp-2">
                        {artifact.name || 'Generazione...'}
                      </h3>
                      <p className="text-xs text-blue-600 font-medium">L'IA sta lavorando...</p>
                    </div>
                  </div>
                );
              }

              // Determine Icon based on type
              let IconComponent = BookOpen;
              let bgColor = 'bg-blue-50';
              let iconColor = 'text-blue-600';
              let borderColor = 'border-gray-100';

              switch (artifact.type) {
                case 'mappa':
                  IconComponent = GitGraph;
                  bgColor = 'bg-purple-50';
                  iconColor = 'text-purple-600';
                  break;
                case 'quiz':
                  IconComponent = Check;
                  bgColor = 'bg-orange-50';
                  iconColor = 'text-orange-600';
                  break;
                case 'flashcard':
                case 'approfondimento': // Handle existing dashboard type
                  IconComponent = Layers;
                  bgColor = 'bg-indigo-50';
                  iconColor = 'text-indigo-600';
                  break;
                case 'sintesi':
                  IconComponent = FileText;
                  bgColor = 'bg-emerald-50';
                  iconColor = 'text-emerald-600';
                  break;
                default:
                  break;
              }

              // Render "Closed Tile" (Card)
              return (
                <div
                  key={artifact.id}
                  onClick={() => setExpandedArtifact(artifact)}
                  className={`group bg-white rounded-2xl border ${borderColor} p-6 hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer flex flex-col items-start h-[220px] relative`}
                >
                  {/* Icon Top Left */}
                  <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                    <IconComponent className={`w-6 h-6 ${iconColor}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 w-full">
                    <div className="flex justify-between items-start">
                      <span className={`text-[10px] font-bold uppercase tracking-wider mb-2 px-2 py-0.5 rounded-full ${bgColor} ${iconColor}`}>
                        {artifact.type}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-800 text-lg leading-snug line-clamp-3 mb-2">
                      {(artifact.content && artifact.content.title) || artifact.title || artifact.name}
                    </h3>
                  </div>

                  <div className="w-full mt-auto pt-4 border-t border-gray-50 flex justify-between items-center text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(artifact.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Rename/Delete Menu Trigger - Simplified for now as two icons */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newName = prompt("Inserisci il nuovo nome:", artifact.name);
                          if (newName) handleRenameArtifact(artifact.id, newName);
                        }}
                        className="p-1.5 rounded-full hover:bg-gray-100 hover:text-blue-600 transition-colors"
                        title="Rinomina"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteArtifact(e, artifact.id)}
                        className="p-1.5 rounded-full hover:bg-red-50 hover:text-red-600 transition-colors"
                        title="Elimina"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ToolConfigModal
        isOpen={!!activeToolModal}
        tool={activeToolModal}
        onClose={() => setActiveToolModal(null)}
        onGenerate={(config) => handleCreateArtifact({ ...config, type: activeToolModal.type, includeChatHistory: false })}
      />
    </div>
  );

  const renderProgressTab = () => (
    <AnalyticsDashboard courseId={courseId} artifactCount={artifacts.length} />
  );

  const renderPracticeTab = () => (
    <div className="space-y-4">
      <ProjectWork
        courseId={courseId}
        title="Project Work"
        emptyTitle="Nessun project work attivo"
        emptyDescription="Il docente non ha ancora attivato il project work per questo corso."
        loadingLabel="Caricamento project work..."
        groupMissingLabel="Il docente non ti ha ancora assegnato a un gruppo per questo project work."
        sectionKey="project-work"
        hideWhenInactive
      />
      <IntermediateTestsPanel courseId={courseId} />
    </div>
  );



  // Full Screen Artifact Modal
  const ArtifactModal = () => {
    if (!expandedArtifact) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 animate-in fade-in duration-200">
        <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 relative">

          <div className="p-4 border-b flex items-center justify-between bg-white z-10">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              {expandedArtifact.title}
            </h3>
            <button
              onClick={(e) => { e.stopPropagation(); setExpandedArtifact(null); }}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-auto bg-slate-50 p-4">
            <ArtifactRenderer
              artifact={expandedArtifact}
              onComplete={handlePlanQuizCompletion}
            />
          </div>
        </div>
      </div>
    );
  };
  // Quiz Modal
  const QuizModal = () => {
    if (!quizActive) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {!showQuizResult ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  Quiz: Funzioni Python
                </h3>
                <div className="text-sm text-gray-600">
                  Domanda {currentQuestion + 1} di {quizQuestions.length}
                </div>
              </div>

              <div className="mb-6">
                <div className="text-lg font-semibold text-gray-800 mb-4">
                  {quizQuestions[currentQuestion].question}
                </div>

                <div className="space-y-3">
                  {quizQuestions[currentQuestion].options.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectAnswer(idx)}
                      disabled={selectedAnswer !== null}
                      className={`w-full p-4 text-left rounded-lg border-2 transition ${selectedAnswer === null
                        ? 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                        : selectedAnswer === idx
                          ? idx === quizQuestions[currentQuestion].correct
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : idx === quizQuestions[currentQuestion].correct
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 bg-gray-50'
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {selectedAnswer !== null && (
                  <div className={`mt-4 p-4 rounded-lg ${selectedAnswer === quizQuestions[currentQuestion].correct
                    ? 'bg-green-50 border-l-4 border-green-500'
                    : 'bg-red-50 border-l-4 border-red-500'
                    }`}>
                    <div className={`font-semibold mb-2 ${selectedAnswer === quizQuestions[currentQuestion].correct
                      ? 'text-green-800'
                      : 'text-red-800'
                      }`}>
                      {selectedAnswer === quizQuestions[currentQuestion].correct
                        ? '✅ Risposta Corretta!'
                        : '❌ Risposta Sbagliata'}
                    </div>
                    <div className="text-sm text-gray-700">
                      {quizQuestions[currentQuestion].explanation}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={closeQuiz}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Chiudi
                </button>
                {selectedAnswer !== null && (
                  <button
                    onClick={nextQuestion}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    {currentQuestion < quizQuestions.length - 1 ? 'Prossima' : 'Risultati'}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="text-6xl mb-4">
                {quizScore >= quizQuestions.length * 0.8 ? '🎉' : quizScore >= quizQuestions.length * 0.6 ? '😊' : '📚'}
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Quiz Completato!
              </h3>
              <div className="text-5xl font-bold text-blue-600 mb-2">
                {quizScore}/{quizQuestions.length}
              </div>
              <div className="text-xl text-gray-600 mb-6">
                {Math.round((quizScore / quizQuestions.length) * 100)}%
              </div>
              <p className="text-gray-600 mb-8">
                {quizScore >= quizQuestions.length * 0.8
                  ? 'Eccellente! Hai padroneggiato l\'argomento! 🎉'
                  : quizScore >= quizQuestions.length * 0.6
                    ? 'Buon lavoro! Ancora qualche punto da rivedere. 😊'
                    : 'Continua a studiare! ALMA ha preparato materiale di ripasso per te. 📚'}
              </p>
              <button
                onClick={closeQuiz}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main Render

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                title="Torna alla selezione"
              >
                <Home className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-blue-900 mb-2">Dashboard ALMA</h1>
                <p className="text-gray-600">
                  Corso ID: {course?.id || 'N/D'} - {course?.title || 'N/D'} - Studente: {user?.name || user?.email || 'Studente'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Percentuale di completamento del corso</div>
              <div className="text-3xl font-bold text-blue-600">
                {analytics?.course_completion || 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-2 mb-6">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { id: 'tutor', icon: Brain, label: 'Tutor' },
              { id: 'lesson-plan', icon: Calendar, label: 'Lesson Plan' },
              { id: 'materials', icon: BookOpen, label: 'Materiali' },
              { id: 'tools', icon: Sparkles, label: 'Tool AI' },
              { id: 'practice', icon: Github, label: 'Pratica' },
              { id: 'progress', icon: TrendingUp, label: 'Progressi' }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg font-medium transition ${activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {activeTab === 'tutor' && renderTutorTab()}
            {activeTab === 'lesson-plan' && renderLessonPlanTab()}
            {activeTab === 'materials' && renderMaterialsTab()}
            {activeTab === 'tools' && renderToolsTab()}
            {activeTab === 'practice' && renderPracticeTab()}
            {activeTab === 'progress' && renderProgressTab()}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Objectives */}
            <div className="bg-white rounded-xl p-5 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Obiettivi Corso
              </h3>
              {objectivesLoading ? (
                <div className="text-center py-4">
                  <Circle className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                  <p className="text-sm text-gray-500 mt-2">Caricamento...</p>
                </div>
              ) : courseObjectives.length > 0 ? (
                <div className="space-y-2">
                  {courseObjectives.map((objective, idx) => {
                    // Handle both string and object formats
                    // Determine title
                    let title = "Obiettivo";
                    if (typeof objective === 'string') {
                      title = objective;
                    } else if (objective && typeof objective === 'object') {
                      // Prioritize 'objective' property as requested by user
                      title = objective.objective || objective.title || objective.description || "Obiettivo indefinito";
                    }

                    const isString = typeof objective === 'string';
                    const isCompleted = !isString && objective.completed;
                    const description = !isString && objective.description && (objective.title || objective.objective) ? objective.description : null;

                    return (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3"
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm ${isCompleted ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                            {title}
                          </p>
                          {description && (
                            <p className="text-xs text-gray-500 mt-1">{description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Nessun obiettivo disponibile</p>
                </div>
              )}
            </div>

            {/* Useful Links */}
            <div className="bg-white rounded-xl p-5 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-600" />
                Link utili
              </h3>
              {materialsLoading ? (
                <div className="text-center py-4">
                  <Circle className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                  <p className="text-sm text-gray-500 mt-2">Caricamento...</p>
                </div>
              ) : courseLinks.length > 0 ? (
                <div className="space-y-2">
                  {courseLinks.map((linkItem) => (
                    <a
                      key={linkItem.id}
                      href={linkItem.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition group"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700 break-words">
                          {linkItem.title}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0 mt-0.5" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Nessun link disponibile</p>
                </div>
              )}
            </div>

            {/* Today Stats */}
            <div className="bg-white rounded-xl p-5 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Oggi
              </h3>
              {[
                {
                  label: 'Contenuti generati',
                  value: artifacts.length,
                  icon: Sparkles
                },
                {
                  label: 'Quiz completati',
                  value: analytics?.completed_tests || 0,
                  icon: CheckCircle2
                },
                {
                  label: 'Completamento Corso',
                  value: `${analytics?.course_completion || 0}%`,
                  icon: TrendingUp
                }
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="flex justify-between items-center p-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">{stat.label}</span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">{stat.value}</span>
                  </div>
                );
              })}
            </div>

            {/* Insight ALMA (Post-it) */}
            <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-5 shadow-lg relative">
              <h3 className="font-bold text-yellow-900 mb-3 flex items-center gap-2 handwriting-font">
                <Edit className="w-4 h-4" />
                Note
              </h3>
              <textarea
                value={userNote}
                onChange={(e) => {
                  const newLine = e.target.value;
                  setUserNote(newLine);
                  const userId = user?.id || 'guest';
                  const storageKey = `almaUserNote_${userId}_${courseId}`;
                  localStorage.setItem(storageKey, newLine);
                }}
                className="w-full bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-yellow-400/50 rounded-lg text-yellow-900 placeholder-yellow-700/50 resize-none text-sm leading-relaxed handwriting-font min-h-[120px] p-2"
                placeholder="Scrivi qui i tuoi appunti o le intuizioni..."
              />
            </div>

            {/* Upcoming */}
            <div className="bg-white rounded-xl p-5 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Prossime Attività
              </h3>
              <div className="space-y-2 text-sm">
                {eventsLoading ? (
                  <div className="text-center py-2 text-gray-500">Caricamento...</div>
                ) : events.length > 0 ? (
                  events.map((item, idx) => {
                    const type = item.type ? item.type.toLowerCase().trim().replace('_', ' ') : 'altro';

                    // Pastel Color Mapping
                    let styles = { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-100' };

                    if (type.includes('lezione')) {
                      styles = { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' };
                    } else if (type.includes('project')) {
                      styles = { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-100' };
                    } else if (type.includes('esercitazione')) {
                      styles = { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' };
                    } else if (type.includes('consegna')) {
                      styles = { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-100' };
                    }

                    const dateObj = new Date(item.start_at);

                    // Format: "Lun 20 Mag, 10:00"
                    const formattedDate = dateObj.toLocaleDateString('it-IT', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    });

                    return (
                      <div key={idx} className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
                        <div className={`font-semibold ${styles.text} capitalize text-xs tracking-wide mb-1 opacity-80`}>
                          {item.type ? item.type.replace('_', ' ') : 'Evento'}
                        </div>
                        <div className="flex justify-between items-start">
                          <div className={`font-medium ${styles.text} text-sm`}>
                            {item.title}
                          </div>
                        </div>
                        <div className={`text-xs ${styles.text} mt-2 font-mono opacity-75 flex items-center gap-1`}>
                          <Calendar className="w-3 h-3" />
                          {formattedDate}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-2">Nessuna attività imminente</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Modals */}
      <ToolConfigModal
        isOpen={!!activeToolModal}
        onClose={() => setActiveToolModal(null)}
        tool={activeToolModal}
        onGenerate={handleCreateArtifact}
      />

      {activeMode !== 'chat' && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
          {/* Floating Chat Button could go here */}
        </div>
      )}

      <ArtifactModal />
      <QuizModal />

      {/* Plan Completion Modal */}
      {showCompletionSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center relative overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Confetti / Decoration Background */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-10">
              <div className="absolute top-10 left-10 text-yellow-500 transform -rotate-12"><Trophy size={60} /></div>
              <div className="absolute bottom-10 right-10 text-blue-500 transform rotate-12"><Star size={60} /></div>
              <div className="absolute top-20 right-20 text-green-500 transform rotate-45"><Sparkles size={40} /></div>
            </div>

            <div className="relative z-10">
              <div className="w-24 h-24 bg-gradient-to-br from-yellow-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Trophy className="w-12 h-12 text-amber-600 drop-shadow-sm" />
              </div>

              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
                Eccellente! 🏆
              </h2>
              <p className="text-gray-600 mb-6">
                Hai completato il Lesson Plan con successo!
              </p>

              <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-gray-100">
                <div className="text-sm text-gray-500 uppercase tracking-wide font-bold mb-1">Punteggio Finale</div>
                <div className="text-5xl font-black text-blue-600">
                  {showCompletionSuccess.score}%
                </div>
              </div>

              <button
                onClick={() => setShowCompletionSuccess(null)}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-200 transition-all transform hover:scale-[1.02]"
              >
                Continua
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limit Warning Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 transform transition-all scale-100 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Limite Lezioni Raggiunto</h3>
              <p className="text-gray-600 mb-6">
                Per garantire la massima qualità dei materiali generati dall'AI, puoi selezionare un massimo di <strong>3 lezioni</strong> alla volta.
              </p>
              <button
                onClick={() => setShowLimitModal(false)}
                className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl transition transform active:scale-95"
              >
                Ho Capito
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlmaDashboard;
